import {createContext, useReducer, type ReactNode} from "react"
import type {
    CropState,
    ImageState,
    EditorMode,
    CropParams,
    Dimensions,
    Position,
    MaterialState,
} from "../types/types"
import {CONSTANTS} from "../constants/constants"

interface CropperState {
    materials: {
        [key: number]: MaterialState
    }
}

type CropperAction =
    | {type: "INIT_MATERIAL"; materialIndex: number; cropDimensions: Dimensions}
    | {
          type: "SET_IMAGE_STATE"
          materialIndex: number
          imageState: Partial<ImageState>
      }
    | {
          type: "SET_CROP_STATE"
          materialIndex: number
          cropState: Partial<CropState>
      }
    | {type: "SET_MODE"; materialIndex: number; mode: EditorMode}
    | {type: "SET_CROP_PARAMS"; materialIndex: number; params: CropParams}
    | {type: "SET_PROCESSING"; materialIndex: number; isProcessing: boolean}
    | {
          type: "SET_DOWNLOAD_URL"
          materialIndex: number
          downloadUrl: string | null
      }
    | {
          type: "SET_UPLOAD_PROGRESS"
          materialIndex: number
          uploadProgress: number
      }
    | {
          type: "SET_CROP_DIMENSIONS"
          materialIndex: number
          dimensions: Dimensions
      }
    | {
          type: "SET_CURRENT_IMAGE_URL"
          materialIndex: number
          currentImageUrl: string | null
      }
    | {
          type: "SET_VIDEO_ELEMENT"
          materialIndex: number
          videoElement: HTMLVideoElement | null
      }
    | {
          type: "SET_RENDER_DATA"
          materialIndex: number
          renderData: {position: Position; dimensions: Dimensions} | null
      }
    | {type: "RESET_MATERIAL"; materialIndex: number}

const initialState: CropperState = {
    materials: {},
}

const createInitialImageState = (): ImageState => ({
    status: "initial",
    dimensions: {width: 0, height: 0},
    position: {x: 0, y: 0},
    zoom: 100,
    videoPlaying: false,
})

const createInitialCropState = (cropDimensions: Dimensions): CropState => ({
    status: "idle",
    isDragging: false,
    dimensions: cropDimensions,
    dragStart: {x: 0, y: 0},
})

const createInitialCropParams = (): CropParams => ({
    offsetX: 0,
    offsetY: 0,
    repeatX: 1,
    repeatY: 1,
    sourceWidth: 0,
    sourceHeight: 0,
    sourceX: 0,
    sourceY: 0,
    sourceCropWidth: 0,
    sourceCropHeight: 0,
    cropWidth: CONSTANTS.DEFAULT_CROP_WIDTH,
    cropHeight: CONSTANTS.DEFAULT_CROP_HEIGHT,
    mode: "crop",
})

const cropperReducer = (
    state: CropperState,
    action: CropperAction
): CropperState => {
    switch (action.type) {
        case "INIT_MATERIAL":
            return {
                ...state,
                materials: {
                    ...state.materials,
                    [action.materialIndex]: {
                        imageState: createInitialImageState(),
                        cropState: createInitialCropState(
                            action.cropDimensions
                        ),
                        mode: "crop",
                        cropParams: createInitialCropParams(),
                        isProcessing: false,
                        downloadUrl: null,
                        uploadProgress: 0,
                        currentImageUrl: null,
                        videoElement: null,
                        renderData: null,
                    },
                },
            }

        case "SET_IMAGE_STATE":
            if (!state.materials[action.materialIndex]) return state

            return {
                ...state,
                materials: {
                    ...state.materials,
                    [action.materialIndex]: {
                        ...state.materials[action.materialIndex],
                        imageState: {
                            ...state.materials[action.materialIndex].imageState,
                            ...action.imageState,
                        } as ImageState,
                    },
                },
            }

        case "SET_CROP_STATE":
            if (!state.materials[action.materialIndex]) return state

            return {
                ...state,
                materials: {
                    ...state.materials,
                    [action.materialIndex]: {
                        ...state.materials[action.materialIndex],
                        cropState: {
                            ...state.materials[action.materialIndex].cropState,
                            ...action.cropState,
                        } as CropState,
                    },
                },
            }

        case "SET_MODE":
            if (!state.materials[action.materialIndex]) return state

            return {
                ...state,
                materials: {
                    ...state.materials,
                    [action.materialIndex]: {
                        ...state.materials[action.materialIndex],
                        mode: action.mode,
                    },
                },
            }

        case "SET_CROP_PARAMS":
            if (!state.materials[action.materialIndex]) return state

            return {
                ...state,
                materials: {
                    ...state.materials,
                    [action.materialIndex]: {
                        ...state.materials[action.materialIndex],
                        cropParams: action.params,
                    },
                },
            }

        case "SET_PROCESSING":
            if (!state.materials[action.materialIndex]) return state

            return {
                ...state,
                materials: {
                    ...state.materials,
                    [action.materialIndex]: {
                        ...state.materials[action.materialIndex],
                        isProcessing: action.isProcessing,
                    },
                },
            }

        case "SET_DOWNLOAD_URL":
            if (!state.materials[action.materialIndex]) return state

            return {
                ...state,
                materials: {
                    ...state.materials,
                    [action.materialIndex]: {
                        ...state.materials[action.materialIndex],
                        downloadUrl: action.downloadUrl,
                    },
                },
            }

        case "SET_UPLOAD_PROGRESS":
            if (!state.materials[action.materialIndex]) return state

            return {
                ...state,
                materials: {
                    ...state.materials,
                    [action.materialIndex]: {
                        ...state.materials[action.materialIndex],
                        uploadProgress: action.uploadProgress,
                    },
                },
            }

        case "SET_CROP_DIMENSIONS":
            if (!state.materials[action.materialIndex]) return state

            return {
                ...state,
                materials: {
                    ...state.materials,
                    [action.materialIndex]: {
                        ...state.materials[action.materialIndex],
                        cropState: {
                            ...state.materials[action.materialIndex].cropState,
                            dimensions: action.dimensions,
                        },
                    },
                },
            }

        case "SET_CURRENT_IMAGE_URL":
            if (!state.materials[action.materialIndex]) return state

            return {
                ...state,
                materials: {
                    ...state.materials,
                    [action.materialIndex]: {
                        ...state.materials[action.materialIndex],
                        currentImageUrl: action.currentImageUrl,
                    },
                },
            }

        case "SET_VIDEO_ELEMENT":
            if (!state.materials[action.materialIndex]) return state

            return {
                ...state,
                materials: {
                    ...state.materials,
                    [action.materialIndex]: {
                        ...state.materials[action.materialIndex],
                        videoElement: action.videoElement,
                    },
                },
            }

        case "SET_RENDER_DATA":
            if (!state.materials[action.materialIndex]) return state

            return {
                ...state,
                materials: {
                    ...state.materials,
                    [action.materialIndex]: {
                        ...state.materials[action.materialIndex],
                        renderData: action.renderData,
                    },
                },
            }

        case "RESET_MATERIAL":
            return {
                ...state,
                materials: {
                    ...state.materials,
                    [action.materialIndex]: {
                        imageState: createInitialImageState(),
                        cropState: createInitialCropState({
                            width: CONSTANTS.DEFAULT_CROP_WIDTH,
                            height: CONSTANTS.DEFAULT_CROP_HEIGHT,
                        }),
                        mode: "crop",
                        cropParams: createInitialCropParams(),
                        isProcessing: false,
                        downloadUrl: null,
                        uploadProgress: 0,
                        currentImageUrl: null,
                        videoElement: null,
                        renderData: null,
                    },
                },
            }

        default:
            return state
    }
}

export const CropperContext = createContext<{
    state: CropperState
    dispatch: React.Dispatch<CropperAction>
} | null>(null)

export const CropperProvider: React.FC<{children: ReactNode}> = ({
    children,
}) => {
    const [state, dispatch] = useReducer(cropperReducer, initialState)

    return (
        <CropperContext.Provider value={{state, dispatch}}>
            {children}
        </CropperContext.Provider>
    )
}
